import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, RefreshCw, ScanFace } from 'lucide-react';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { useAdminData } from '../../context/AdminDataContext';
import { useRole } from '../../context/RoleContext';
import { listClientTimeEvents } from '../../lib/api/client';
import { measureProductAction } from '../../lib/productAnalytics';
import { ApiTimeEvent } from '../../lib/api/contracts';

const formatTime = (value: string) => new Intl.DateTimeFormat('en-ZA', {
  dateStyle: 'medium', timeStyle: 'medium', timeZone: 'Africa/Johannesburg',
}).format(new Date(value));

const Timesheets: React.FC = () => {
  const { activeCompanyId } = useRole();
  const { clients } = useAdminData();
  const [events, setEvents] = useState<ApiTimeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeClient = clients.find((client) => client.id === activeCompanyId) ?? clients[0] ?? null;

  const loadEvents = useCallback(async (quiet = false, manual = false) => {
    if (!activeClient) {
      setEvents([]);
      setLoading(false);
      return;
    }
    if (!quiet) setLoading(true);
    try {
      const request = () => listClientTimeEvents({ client_id: activeClient.id, limit: 100 });
      const rows = await (manual ? measureProductAction('tracking_refresh', request) : request());
      setEvents(rows ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load attendance records.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [activeClient]);

  useEffect(() => {
    void loadEvents();
    const timer = window.setInterval(() => void loadEvents(true), 5000);
    return () => window.clearInterval(timer);
  }, [loadEvents]);

  const clockedInNow = useMemo(() => {
    const latest = new Map<string, ApiTimeEvent>();
    events.forEach((event) => {
      if (!latest.has(event.employee_id)) latest.set(event.employee_id, event);
    });
    return Array.from(latest.values()).filter((event) => event.event_type === 'CLOCK_IN').length;
  }, [events]);

  return (
    <div className="space-y-6">
      <section className="admin-page-header">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--admin-muted)]">Live attendance</p>
          <h1 className="text-3xl font-semibold">Tracking</h1>
          <p className="mt-1 text-sm text-[var(--admin-muted)]">Verified clock activity for {activeClient?.name ?? 'the selected client'}.</p>
        </div>
        <button type="button" onClick={() => void loadEvents(false, true)} className="inline-flex items-center gap-2 rounded-xl border border-[var(--admin-border)] px-4 py-2 text-sm font-semibold transition hover:bg-[var(--admin-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--admin-primary)]">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </section>

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="flex items-center gap-4 pt-5 md:pt-6"><Clock3 className="text-[var(--admin-primary)]" /><div><p className="text-2xl font-semibold">{events.length}</p><p className="text-sm text-[var(--admin-muted)]">Recent events</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 pt-5 md:pt-6"><CheckCircle2 className="text-emerald-500" /><div><p className="text-2xl font-semibold">{clockedInNow}</p><p className="text-sm text-[var(--admin-muted)]">Currently clocked in</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 pt-5 md:pt-6"><ScanFace className="text-sky-500" /><div><p className="text-2xl font-semibold">{events.filter((event) => event.verification === 'FACE').length}</p><p className="text-sm text-[var(--admin-muted)]">Face verified</p></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between"><CardTitle>Attendance stream</CardTitle><span className="inline-flex items-center gap-2 text-xs text-[var(--admin-muted)]"><span className="h-2 w-2 rounded-full bg-emerald-500" />Updates every 5 seconds</span></CardHeader>
        <CardContent>
          {error ? <div role="alert" className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</div> : null}
          {!error && loading ? <div className="py-12 text-center text-sm text-[var(--admin-muted)]">Loading attendance records...</div> : null}
          {!error && !loading && events.length === 0 ? <div className="py-14 text-center"><ScanFace className="mx-auto mb-3 text-[var(--admin-muted)]" /><p className="font-semibold">No clock events yet</p><p className="mt-1 text-sm text-[var(--admin-muted)]">The next verified kiosk clock-in will appear here automatically.</p></div> : null}
          {!error && events.length > 0 ? (
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Event</TableHead><TableHead>Time</TableHead><TableHead>Device</TableHead><TableHead>Verification</TableHead></TableRow></TableHeader>
              <TableBody>{events.map((event) => <TableRow key={event.id}>
                <TableCell><p className="font-semibold">{event.employee_name || event.employee_id}</p><p className="text-xs text-[var(--admin-muted)]">{event.employee_code || event.employee_id}</p></TableCell>
                <TableCell><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${event.event_type === 'CLOCK_IN' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>{event.event_type === 'CLOCK_IN' ? 'Clock in' : 'Clock out'}</span></TableCell>
                <TableCell>{formatTime(event.occurred_at)}</TableCell>
                <TableCell><p>{event.device_name || 'Kiosk'}</p><p className="max-w-40 truncate text-xs text-[var(--admin-muted)]">{event.device_id}</p></TableCell>
                <TableCell><span className="inline-flex items-center gap-1.5 text-sm"><ScanFace size={15} />Face</span></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default Timesheets;
